import { useEffect, useRef, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { ArrowLeft, Plus, Trash2, FileText, Loader2 } from 'lucide-react';
import { JobSearchComboBox } from '@/components/shared/job-search-combo-box';
import type { JobOption } from '@/components/shared/job-search-combo-box';
import { LocationSearchComboBox } from '@/components/shared/location-search-combo-box';
import type { LocationOption } from '@/components/shared/location-search-combo-box';
import { Button } from '@/app/components/ui/button';
import { Input } from '@/app/components/ui/input';
import { Textarea } from '@/app/components/ui/textarea';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/app/components/ui/card';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/app/components/ui/select';
import { Label } from '@/app/components/ui/label';
import { Checkbox } from '@/app/components/ui/checkbox';
import { Badge } from '@/app/components/ui/badge';
import { useCreateJSA, useCreateJSAFromTemplate, useSafetyTemplates } from '@/hooks/use-safety';
import { useOrganizationSettings, useOrgToday } from '@/hooks/use-settings';
import { OfflineIndicator } from '@/app/components/offline/offline-indicator';
import type { RiskLevel, SafetyTemplate } from '@/lib/types';
import { PreviousDayJSAPrompt } from '@/app/components/safety/previous-day-jsa-prompt';

// PPE Checklist Items

interface PPEItem {
  id: string;
  label: string;
  checked: boolean;
  required: boolean;
}

const DEFAULT_PPE_ITEMS: Omit<PPEItem, 'checked' | 'required'>[] = [
  { id: 'harness', label: 'Full body harness (EN 361)' },
  { id: 'helmet', label: 'Helmet with chin strap' },
  { id: 'glasses', label: 'Safety glasses / goggles' },
  { id: 'gloves_general', label: 'Gloves (general)' },
  { id: 'gloves_chemical', label: 'Chemical-resistant gloves' },
  { id: 'boots', label: 'Steel-toe boots' },
  { id: 'highvis', label: 'High-vis vest/clothing' },
  { id: 'hearing', label: 'Hearing protection' },
  { id: 'respirator', label: 'Respirator / dust mask' },
  { id: 'lanyard', label: 'Fall arrest lanyard' },
  { id: 'knife', label: 'Rescue knife' },
  { id: 'radio', label: 'Communication radio' },
];

function createDefaultPPEState(): PPEItem[] {
  return DEFAULT_PPE_ITEMS.map((item) => ({
    ...item,
    checked: false,
    required: true,
  }));
}

function serializePPE(items: PPEItem[], otherPPE: string): string {
  const lines: string[] = [];
  const checked = items.filter((i) => i.checked);
  if (checked.length > 0) {
    const requiredItems = checked.filter((i) => i.required);
    if (requiredItems.length > 0) {
      lines.push('Required:');
      requiredItems.forEach((i) => lines.push('  - ' + i.label));
    }
    const optional = checked.filter((i) => !i.required);
    if (optional.length > 0) {
      lines.push('Optional:');
      optional.forEach((i) => lines.push('  - ' + i.label));
    }
  }
  if (otherPPE.trim()) {
    lines.push('Other:');
    lines.push('  ' + otherPPE.trim());
  }
  return lines.join('\n');
}

function parsePPEFromText(text: string): { items: PPEItem[]; other: string } {
  const items = createDefaultPPEState();
  let other = '';
  if (!text) return { items, other };

  const lower = text.toLowerCase();
  for (const item of items) {
    const labelLower = item.label.toLowerCase();
    const keywords = labelLower.split(/[\s/()]+/).filter((w) => w.length > 3);
    if (keywords.some((kw) => lower.includes(kw))) {
      item.checked = true;
    }
  }

  const otherMatch = text.match(/Other:\s*([\s\S]*?)$/i);
  if (otherMatch) other = otherMatch[1].trim();

  if (lower.includes('optional:')) {
    let optEnd = lower.indexOf('other:');
    if (optEnd === -1) optEnd = text.length;
    const optSection = text.substring(lower.indexOf('optional:'), optEnd);
    for (const item of items) {
      if (item.checked) {
        const labelLower = item.label.toLowerCase();
        const keywords = labelLower.split(/[\s/()]+/).filter((w) => w.length > 3);
        if (keywords.some((kw) => optSection.toLowerCase().includes(kw))) {
          item.required = false;
        }
      }
    }
  }

  return { items, other };
}

// Emergency Procedures

interface EmergencyInfo {
  site_supervisor: string;
  safety_officer: string;
  client_contact: string;
  hospital_name: string;
  hospital_address: string;
  hospital_phone: string;
  hospital_route: string;
  first_aid_location: string;
  muster_point: string;
  shift_supervisor: string;
  emergency_services: string;
  additional_procedures: string;
}

const EMPTY_EMERGENCY: EmergencyInfo = {
  site_supervisor: '',
  safety_officer: '',
  client_contact: '',
  hospital_name: '',
  hospital_address: '',
  hospital_phone: '',
  hospital_route: '',
  first_aid_location: '',
  muster_point: '',
  shift_supervisor: '',
  emergency_services: '',
  additional_procedures: '',
};

function serializeEmergency(info: EmergencyInfo): string {
  const lines: string[] = [];
  if (info.site_supervisor) lines.push('Site Supervisor: ' + info.site_supervisor);
  if (info.safety_officer) lines.push('Safety Officer: ' + info.safety_officer);
  if (info.client_contact) lines.push('Client Contact: ' + info.client_contact);
  if (info.hospital_name || info.hospital_address || info.hospital_phone) {
    lines.push('Nearest Hospital: ' + info.hospital_name);
    if (info.hospital_address) lines.push('  Address: ' + info.hospital_address);
    if (info.hospital_phone) lines.push('  Phone: ' + info.hospital_phone);
    if (info.hospital_route) lines.push('  Route: ' + info.hospital_route);
  }
  if (info.first_aid_location) lines.push('First Aid Location: ' + info.first_aid_location);
  if (info.muster_point) lines.push('Muster Point: ' + info.muster_point);
  if (info.shift_supervisor) lines.push('Shift Supervisor: ' + info.shift_supervisor);
  if (info.emergency_services) lines.push('Emergency Services: ' + info.emergency_services);
  if (info.additional_procedures) {
    lines.push('');
    lines.push('Additional Procedures:');
    lines.push(info.additional_procedures);
  }
  return lines.join('\n');
}

function parseEmergencyFromText(text: string): EmergencyInfo {
  const info = { ...EMPTY_EMERGENCY };
  if (!text) return info;

  const getField = (label: string): string => {
    const regex = new RegExp(label + ':\\s*(.+)', 'i');
    const match = text.match(regex);
    return match ? match[1].trim() : '';
  };

  info.site_supervisor = getField('Site Supervisor');
  info.safety_officer = getField('Safety Officer');
  info.client_contact = getField('Client Contact');
  info.hospital_name = getField('Nearest Hospital');
  info.hospital_address = getField('Address');
  info.hospital_phone = getField('Phone');
  info.hospital_route = getField('Route');
  info.first_aid_location = getField('First Aid Location');
  info.muster_point = getField('Muster Point');
  info.shift_supervisor = getField('Shift Supervisor');
  info.emergency_services = getField('Emergency Services');

  const addMatch = text.match(/Additional Procedures:\s*([\s\S]*?)$/i);
  if (addMatch) info.additional_procedures = addMatch[1].trim();

  return info;
}

// Hazard Row

interface HazardRow {
  step_number: number;
  task_description: string;
  hazard_description: string;
  risk_level: RiskLevel;
  control_measures: string;
  residual_risk_level: string;
  order: number;
}

// Template Picker

function TemplatePicker({
  onSelect,
  onSkip,
}: {
  onSelect: (template: SafetyTemplate) => void;
  onSkip: () => void;
}) {
  const { data: templatesData, isLoading } = useSafetyTemplates({
    template_type: 'jsa',
    page: 1,
    page_size: 50,
  });

  const templates = templatesData?.results ?? [];

  return (
    <div className="space-y-6">
      <div className="text-center">
        <h2 className="text-lg font-semibold text-gray-900">Start from a Template</h2>
        <p className="text-sm text-gray-600 mt-1">
          Choose a template to pre-fill hazards, PPE, and emergency procedures — or start from scratch.
        </p>
      </div>

      {isLoading ? (
        <div className="flex justify-center py-8">
          <Loader2 className="h-6 w-6 animate-spin text-gray-400" />
        </div>
      ) : templates.length === 0 ? (
        <div className="text-center py-8">
          <p className="text-sm text-gray-500">No templates available yet.</p>
        </div>
      ) : (
        <div className="grid gap-3 sm:grid-cols-2">
          {templates.map((template) => (
            <button
              key={template.id}
              type="button"
              className="text-left border rounded-lg p-4 hover:border-primary hover:bg-primary/5 transition-colors"
              onClick={() => onSelect(template)}
            >
              <div className="flex items-start justify-between">
                <div className="flex-1 min-w-0">
                  <p className="font-medium text-sm truncate">{template.name}</p>
                  <p className="text-xs text-gray-500 mt-1 line-clamp-2">{template.description}</p>
                </div>
                <FileText className="h-4 w-4 text-gray-400 ml-2 shrink-0 mt-0.5" />
              </div>
              <div className="flex gap-1.5 mt-2">
                {(template.default_hazards?.length ?? 0) > 0 && (
                  <Badge variant="secondary" className="text-xs">
                    {template.default_hazards.length} hazards
                  </Badge>
                )}
                {template.default_ppe_requirements && (
                  <Badge variant="secondary" className="text-xs">PPE</Badge>
                )}
                {template.default_emergency_procedures && (
                  <Badge variant="secondary" className="text-xs">Emergency</Badge>
                )}
              </div>
            </button>
          ))}
        </div>
      )}

      <div className="flex justify-center">
        <Button type="button" variant="outline" onClick={onSkip}>
          Start from Scratch
        </Button>
      </div>
    </div>
  );
}

// Main Page

export function CreateJSAPage() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const createMutation = useCreateJSA();
  const createFromTemplateMutation = useCreateJSAFromTemplate();
  const orgToday = useOrgToday();
  const { data: organizationSettings } = useOrganizationSettings();
  const emergencyServicesEdited = useRef(false);

  const isDuplicate = searchParams.get('duplicate') === 'true';
  const [showTemplatePicker, setShowTemplatePicker] = useState(!isDuplicate);
  const [selectedTemplateId, setSelectedTemplateId] = useState<number | null>(null);

  // Job Details
  const [title, setTitle] = useState(searchParams.get('title') || '');
  const [selectedProject, setSelectedProject] = useState<JobOption | null>(null);
  const [selectedLocation, setSelectedLocation] = useState<LocationOption | null>(null);
  const [location, setLocation] = useState(searchParams.get('location') || '');
  const [date, setDate] = useState(orgToday);
  const [scopeOfWork, setScopeOfWork] = useState(searchParams.get('scope_of_work') || '');

  // Weather (blank for duplicates — needs daily update)
  const [weatherConditions, setWeatherConditions] = useState('');
  const [windSpeed, setWindSpeed] = useState('');
  const [temperature, setTemperature] = useState('');

  // Hazards
  const [hazards, setHazards] = useState<HazardRow[]>(() => {
    const h = searchParams.get('hazards');
    if (h) {
      try { return JSON.parse(h); } catch { return []; }
    }
    return [];
  });

  // PPE Checklist
  const [ppeItems, setPpeItems] = useState<PPEItem[]>(() => {
    const ppe = searchParams.get('ppe_requirements');
    if (ppe) return parsePPEFromText(ppe).items;
    return createDefaultPPEState();
  });
  const [otherPPE, setOtherPPE] = useState(() => {
    const ppe = searchParams.get('ppe_requirements');
    if (ppe) return parsePPEFromText(ppe).other;
    return '';
  });

  // Emergency Procedures
  const [emergency, setEmergency] = useState<EmergencyInfo>(() => {
    const ep = searchParams.get('emergency_procedures');
    if (ep) return parseEmergencyFromText(ep);
    return { ...EMPTY_EMERGENCY };
  });

  useEffect(() => {
    if (!organizationSettings || emergencyServicesEdited.current) return;
    setEmergency((current) => (
      current.emergency_services
        ? current
        : {
            ...current,
            emergency_services: organizationSettings.emergency_services_number,
          }
    ));
  }, [organizationSettings]);

  const updateEmergency = (field: keyof EmergencyInfo, value: string) => {
    if (field === 'emergency_services') {
      emergencyServicesEdited.current = true;
    }
    setEmergency((prev) => ({ ...prev, [field]: value }));
  };

  // Template Selection

  const handleTemplateSelect = (template: SafetyTemplate) => {
    setSelectedTemplateId(template.id);

    if (template.default_hazards?.length) {
      setHazards(
        template.default_hazards.map((h, i) => ({
          step_number: h.step_number || i + 1,
          task_description: h.task_description || '',
          hazard_description: h.hazard_description || '',
          risk_level: h.risk_level || 'medium',
          control_measures: h.control_measures || '',
          residual_risk_level: h.residual_risk_level || 'low',
          order: h.order ?? i,
        })),
      );
    }

    if (template.default_ppe_requirements) {
      const parsed = parsePPEFromText(template.default_ppe_requirements);
      setPpeItems(parsed.items);
      setOtherPPE(parsed.other);
    }

    if (template.default_emergency_procedures) {
      const parsed = parseEmergencyFromText(template.default_emergency_procedures);
      setEmergency({
        ...parsed,
        emergency_services:
          parsed.emergency_services
          || organizationSettings?.emergency_services_number
          || '',
      });
    }

    setShowTemplatePicker(false);
  };

  // Hazard Helpers

  const addHazard = () => {
    setHazards([
      ...hazards,
      {
        step_number: hazards.length + 1,
        task_description: '',
        hazard_description: '',
        risk_level: 'medium',
        control_measures: '',
        residual_risk_level: 'low',
        order: hazards.length,
      },
    ]);
  };

  const removeHazard = (index: number) => {
    setHazards(hazards.filter((_, i) => i !== index));
  };

  const updateHazard = (index: number, field: keyof HazardRow, value: string) => {
    const updated = [...hazards];
    updated[index] = { ...updated[index], [field]: value };
    setHazards(updated);
  };

  // PPE Helpers

  const togglePPEItem = (id: string) => {
    setPpeItems((prev) =>
      prev.map((item) => (item.id === id ? { ...item, checked: !item.checked } : item)),
    );
  };

  const togglePPERequired = (id: string) => {
    setPpeItems((prev) =>
      prev.map((item) => (item.id === id ? { ...item, required: !item.required } : item)),
    );
  };

  // Submit

  const isPending = createMutation.isPending || createFromTemplateMutation.isPending;

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!selectedProject) return;
    try {
      const payload = {
        title,
        project: selectedProject.id,
        project_name: selectedProject.name,
        project_number: selectedProject.job_number,
        location_fk: selectedLocation?.id || null,
        location,
        date,
        scope_of_work: scopeOfWork,
        ppe_requirements: serializePPE(ppeItems, otherPPE),
        emergency_procedures: serializeEmergency(emergency),
        weather_conditions: weatherConditions,
        wind_speed: windSpeed,
        temperature,
        hazards: hazards.map((h, i) => ({ ...h, step_number: i + 1, order: i })),
      };

      let jsa;
      if (selectedTemplateId) {
        jsa = await createFromTemplateMutation.mutateAsync({
          ...payload,
          template_id: selectedTemplateId,
        });
      } else {
        jsa = await createMutation.mutateAsync(payload);
      }
      navigate('/safety/jsas/' + jsa.id);
    } catch {
      // error handled by mutation
    }
  };

  // Template Picker Screen

  if (showTemplatePicker) {
    return (
      <div className="p-4 sm:p-6 lg:p-8 space-y-6 max-w-4xl mx-auto">
        <div className="flex items-center gap-4">
          <Button variant="ghost" size="icon" onClick={() => navigate('/safety/jsas')}>
            <ArrowLeft className="h-5 w-5" />
          </Button>
          <div>
            <h1 className="text-2xl font-bold text-gray-900">New Project Safety Analysis</h1>
            <p className="text-gray-600">Create a new JSA for your project</p>
          </div>
        </div>

        <OfflineIndicator />

        <Card>
          <CardContent className="pt-6">
            <TemplatePicker
              onSelect={handleTemplateSelect}
              onSkip={() => setShowTemplatePicker(false)}
            />
          </CardContent>
        </Card>
      </div>
    );
  }

  // JSA Form

  return (
    <div className="p-4 sm:p-6 lg:p-8 space-y-6 max-w-4xl mx-auto">
      <PreviousDayJSAPrompt projectId={selectedProject?.id ?? null} />
      <div className="flex items-center gap-4">
        <Button variant="ghost" size="icon" onClick={() => navigate('/safety/jsas')}>
          <ArrowLeft className="h-5 w-5" />
        </Button>
        <div>
          <h1 className="text-2xl font-bold text-gray-900">
            {isDuplicate ? 'Duplicate JSA' : 'New Project Safety Analysis'}
          </h1>
          <p className="text-gray-600">
            {isDuplicate
              ? 'Copied from previous JSA — update weather and review hazards'
              : 'Create a new JSA for your project'}
          </p>
        </div>
      </div>

      <OfflineIndicator />

      <form onSubmit={handleSubmit} className="space-y-6">
        {/* Emergency Procedures (moved to top) */}
        <Card>
          <CardHeader>
            <CardTitle>Emergency Procedures</CardTitle>
            <CardDescription>Emergency contacts and procedures for the project site</CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
              <div>
                <Label htmlFor="site_supervisor">Site Supervisor</Label>
                <Input
                  id="site_supervisor"
                  value={emergency.site_supervisor}
                  onChange={(e) => updateEmergency('site_supervisor', e.target.value)}
                  placeholder="Name / phone"
                />
              </div>
              <div>
                <Label htmlFor="safety_officer">Safety Officer</Label>
                <Input
                  id="safety_officer"
                  value={emergency.safety_officer}
                  onChange={(e) => updateEmergency('safety_officer', e.target.value)}
                  placeholder="Name / phone"
                />
              </div>
              <div>
                <Label htmlFor="client_contact">Client Contact</Label>
                <Input
                  id="client_contact"
                  value={emergency.client_contact}
                  onChange={(e) => updateEmergency('client_contact', e.target.value)}
                  placeholder="Name / phone"
                />
              </div>
            </div>

            <div className="border-t pt-4">
              <Label className="text-sm font-medium mb-2 block">Nearest Hospital</Label>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div>
                  <Label htmlFor="hospital_name" className="text-xs text-gray-500">Name</Label>
                  <Input
                    id="hospital_name"
                    value={emergency.hospital_name}
                    onChange={(e) => updateEmergency('hospital_name', e.target.value)}
                  />
                </div>
                <div>
                  <Label htmlFor="hospital_phone" className="text-xs text-gray-500">Phone</Label>
                  <Input
                    id="hospital_phone"
                    value={emergency.hospital_phone}
                    onChange={(e) => updateEmergency('hospital_phone', e.target.value)}
                  />
                </div>
                <div className="sm:col-span-2">
                  <Label htmlFor="hospital_address" className="text-xs text-gray-500">Address</Label>
                  <Input
                    id="hospital_address"
                    value={emergency.hospital_address}
                    onChange={(e) => updateEmergency('hospital_address', e.target.value)}
                  />
                </div>
                <div className="sm:col-span-2">
                  <Label htmlFor="hospital_route" className="text-xs text-gray-500">Route / Directions</Label>
                  <Input
                    id="hospital_route"
                    value={emergency.hospital_route}
                    onChange={(e) => updateEmergency('hospital_route', e.target.value)}
                  />
                </div>
              </div>
            </div>

            <div className="border-t pt-4 grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div>
                <Label htmlFor="first_aid">First Aid Location</Label>
                <Input
                  id="first_aid"
                  value={emergency.first_aid_location}
                  onChange={(e) => updateEmergency('first_aid_location', e.target.value)}
                  placeholder="e.g., Site office, ground floor"
                />
              </div>
              <div>
                <Label htmlFor="muster_point">Muster Point</Label>
                <Input
                  id="muster_point"
                  value={emergency.muster_point}
                  onChange={(e) => updateEmergency('muster_point', e.target.value)}
                  placeholder="e.g., Car park entrance"
                />
              </div>
              <div>
                <Label htmlFor="shift_supervisor">Shift Supervisor on Duty</Label>
                <Input
                  id="shift_supervisor"
                  value={emergency.shift_supervisor}
                  onChange={(e) => updateEmergency('shift_supervisor', e.target.value)}
                />
              </div>
              <div>
                <Label htmlFor="emergency_services">Emergency Services Number</Label>
                <Input
                  id="emergency_services"
                  value={emergency.emergency_services}
                  onChange={(e) => updateEmergency('emergency_services', e.target.value)}
                />
              </div>
            </div>

            <div className="border-t pt-4">
              <Label htmlFor="additional_procedures">Additional Procedures</Label>
              <Textarea
                id="additional_procedures"
                value={emergency.additional_procedures}
                onChange={(e) => updateEmergency('additional_procedures', e.target.value)}
                rows={3}
                placeholder="Any scenario-specific emergency procedures..."
              />
            </div>
          </CardContent>
        </Card>

        {/* Project Details */}
        <Card>
          <CardHeader>
            <CardTitle>Project Details</CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div>
              <Label htmlFor="title">Title *</Label>
              <Input
                id="title"
                value={title}
                onChange={(e) => setTitle(e.target.value)}
                placeholder="e.g., Window Replacement — East Face Level 12"
                required
              />
            </div>
            <div>
              <Label>Project *</Label>
              <JobSearchComboBox
                value={selectedProject}
                required
                onChange={(job) => {
                  setSelectedProject(job);
                  if (job?.location_fk_display && !selectedLocation) {
                    setSelectedLocation({
                      id: job.location_fk_display.id,
                      name: job.location_fk_display.name,
                      type: job.location_fk_display.type,
                    });
                  }
                }}
              />
            </div>
            <div>
              <Label>Location</Label>
              <LocationSearchComboBox value={selectedLocation} onChange={setSelectedLocation} />
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div>
                <Label htmlFor="location">Specific Area / Details</Label>
                <Input
                  id="location"
                  value={location}
                  onChange={(e) => setLocation(e.target.value)}
                  placeholder="e.g. East face, 3rd floor scaffolding"
                />
              </div>
              <div>
                <Label htmlFor="date">Date *</Label>
                <Input
                  id="date"
                  type="date"
                  value={date}
                  onChange={(e) => setDate(e.target.value)}
                  required
                />
              </div>
            </div>
            <div>
              <Label htmlFor="scope">Scope of Work</Label>
              <Textarea
                id="scope"
                value={scopeOfWork}
                onChange={(e) => setScopeOfWork(e.target.value)}
                rows={3}
              />
            </div>
          </CardContent>
        </Card>

        {/* Weather and Conditions */}
        <Card>
          <CardHeader>
            <CardTitle>Weather &amp; Conditions</CardTitle>
            {isDuplicate && (
              <CardDescription>Weather fields are blank — update for today</CardDescription>
            )}
          </CardHeader>
          <CardContent className="space-y-4">
            <div>
              <Label htmlFor="weather">Weather Conditions</Label>
              <Input
                id="weather"
                value={weatherConditions}
                onChange={(e) => setWeatherConditions(e.target.value)}
                placeholder="e.g., Clear, partly cloudy"
              />
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div>
                <Label htmlFor="wind">Wind Speed</Label>
                <Input
                  id="wind"
                  value={windSpeed}
                  onChange={(e) => setWindSpeed(e.target.value)}
                  placeholder="e.g., 15 km/h"
                />
              </div>
              <div>
                <Label htmlFor="temp">Temperature</Label>
                <Input
                  id="temp"
                  value={temperature}
                  onChange={(e) => setTemperature(e.target.value)}
                  placeholder="e.g., 22C"
                />
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Hazard Identification */}
        <Card>
          <CardHeader className="flex flex-row items-center justify-between">
            <CardTitle>Hazard Identification</CardTitle>
            <Button type="button" variant="outline" size="sm" onClick={addHazard}>
              <Plus className="h-4 w-4 mr-1" />
              Add Hazard
            </Button>
          </CardHeader>
          <CardContent className="space-y-4">
            {hazards.length === 0 ? (
              <p className="text-gray-500 text-center py-4">
                No hazards added yet. Click &quot;Add Hazard&quot; to begin.
              </p>
            ) : (
              hazards.map((hazard, index) => (
                <div key={index} className="border rounded-lg p-4 space-y-3">
                  <div className="flex items-center justify-between">
                    <span className="font-medium text-sm">Hazard #{index + 1}</span>
                    <Button
                      type="button"
                      variant="ghost"
                      size="icon"
                      onClick={() => removeHazard(index)}
                    >
                      <Trash2 className="h-4 w-4 text-red-500" />
                    </Button>
                  </div>
                  <div>
                    <Label>Task Description</Label>
                    <Input
                      value={hazard.task_description}
                      onChange={(e) => updateHazard(index, 'task_description', e.target.value)}
                    />
                  </div>
                  <div>
                    <Label>Hazard Description</Label>
                    <Input
                      value={hazard.hazard_description}
                      onChange={(e) => updateHazard(index, 'hazard_description', e.target.value)}
                    />
                  </div>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                    <div>
                      <Label>Risk Level</Label>
                      <Select
                        value={hazard.risk_level}
                        onValueChange={(value) => updateHazard(index, 'risk_level', value)}
                      >
                        <SelectTrigger>
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="low">Low</SelectItem>
                          <SelectItem value="medium">Medium</SelectItem>
                          <SelectItem value="high">High</SelectItem>
                          <SelectItem value="critical">Critical</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                    <div>
                      <Label>Control Measures</Label>
                      <Input
                        value={hazard.control_measures}
                        onChange={(e) => updateHazard(index, 'control_measures', e.target.value)}
                      />
                    </div>
                  </div>
                </div>
              ))
            )}
          </CardContent>
        </Card>

        {/* PPE Checklist */}
        <Card>
          <CardHeader>
            <CardTitle>PPE Requirements</CardTitle>
            <CardDescription>Check required gear and toggle Required / Optional</CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              {ppeItems.map((item) => (
                <div
                  key={item.id}
                  className="flex items-center gap-3 rounded-md border p-3"
                >
                  <Checkbox
                    id={'ppe-' + item.id}
                    checked={item.checked}
                    onCheckedChange={() => togglePPEItem(item.id)}
                  />
                  <label
                    htmlFor={'ppe-' + item.id}
                    className="flex-1 text-sm cursor-pointer select-none"
                  >
                    {item.label}
                  </label>
                  {item.checked && (
                    <button
                      type="button"
                      className="text-xs px-2 py-0.5 rounded-full border"
                      style={{
                        backgroundColor: item.required ? 'rgb(220 38 38 / 0.1)' : 'rgb(59 130 246 / 0.1)',
                        color: item.required ? 'rgb(185 28 28)' : 'rgb(29 78 216)',
                        borderColor: item.required ? 'rgb(252 165 165)' : 'rgb(147 197 253)',
                      }}
                      onClick={() => togglePPERequired(item.id)}
                    >
                      {item.required ? 'Required' : 'Optional'}
                    </button>
                  )}
                </div>
              ))}
            </div>
            <div>
              <Label htmlFor="otherPPE">Other PPE</Label>
              <Input
                id="otherPPE"
                value={otherPPE}
                onChange={(e) => setOtherPPE(e.target.value)}
                placeholder="Any additional PPE not listed above..."
              />
            </div>
          </CardContent>
        </Card>

        {/* Submit */}
        <div className="flex justify-end gap-3">
          <Button type="button" variant="outline" onClick={() => navigate('/safety/jsas')}>
            Cancel
          </Button>
          <Button type="submit" disabled={isPending}>
            {isPending ? 'Creating...' : 'Create JSA'}
          </Button>
        </div>
      </form>
    </div>
  );
}
